<template>
  <div class="ai-assistant-container" :style="containerStyle" :class="{ 'is-dragging': isDragging }">
    <!-- Janela de Chat Expansível -->
    <transition name="ai-chat-slide">
      <div v-if="isOpen" class="ai-chat-window shadow-lg" :class="{ 'is-maximized': isMaximized }">
        <!-- Header do Chat (Área de Arraste da Janela) -->
        <div class="ai-chat-header d-flex align-items-center justify-content-between" @mousedown="startDrag"
          @touchstart="startDrag">
          <div class="d-flex align-items-center unselectable">
            <div class="ai-avatar-badge mr-2">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="url(#ai-grad-header)" />
                <path d="M12 6V8M12 16V18M6 12H8M16 12H18" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" />
                <circle cx="9" cy="11" r="1.5" fill="#FFFFFF" />
                <circle cx="15" cy="11" r="1.5" fill="#FFFFFF" />
                <path d="M9.5 15C10.2 15.8 11.1 16 12 16C12.9 16 13.8 15.8 14.5 15" stroke="#FFFFFF" stroke-width="1.5"
                  stroke-linecap="round" />
                <defs>
                  <linearGradient id="ai-grad-header" x1="2" y1="2" x2="22" y2="22" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#FF7E29" />
                    <stop offset="1" stop-color="#974900" />
                  </linearGradient>
                </defs>
              </svg>
              <span class="online-indicator"></span>
            </div>
            <div>
              <h6 class="mb-0 text-white font-weight-bold">MyOracle AI Assistant</h6>
              <small class="text-white-50">Online • Arraste para mover ⇄</small>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <!-- Botão Maximizar / Restaurar -->
            <button class="btn btn-sm btn-icon text-white mr-1"
              :title="isMaximized ? 'Restaurar tamanho' : 'Maximizar janela'" @click.stop="toggleMaximize">
              <svg v-if="!isMaximized" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2">
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
              </svg>
              <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M10 14l-7 7" />
              </svg>
            </button>

            <!-- Botão Nova Conversa -->
            <button class="btn btn-sm btn-icon text-white mr-1" title="Nova Conversa" @click.stop="resetConversation">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>

            <!-- Botão Minimizar / Fechar -->
            <button class="btn btn-sm btn-icon text-white" title="Minimizar / Fechar" @click.stop="toggleChat">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        <!-- Conteúdo de Mensagens -->
        <div class="ai-chat-body" ref="chatBody">
          <!-- Mensagem Inicial de Boas-Vindas -->
          <div class="ai-msg-row ai-msg-bot">
            <div class="ai-msg-avatar">🤖</div>
            <div class="ai-msg-bubble">
              <p class="mb-1">Olá, <strong>{{ userName }}</strong>! 👋</p>
              <p class="mb-0">Sou a IA do **MyOracle** integrada ao **MyConfig**. Como posso ajudar no seu processo
                fabril hoje?</p>
            </div>
          </div>

          <!-- Sugestões Rápidas (quando poucas mensagens) -->
          <div v-if="messages.length === 0" class="ai-suggestions-wrapper mt-3 mb-2">
            <small class="text-muted font-weight-bold d-block mb-2">Sugestões de perguntas:</small>
            <button v-for="(suggestion, idx) in suggestions" :key="idx"
              class="btn btn-outline-primary btn-sm rounded-pill text-left mb-2 mr-1 ai-suggestion-chip"
              @click="sendSuggestion(suggestion.text)">
              {{ suggestion.icon }} {{ suggestion.text }}
            </button>
          </div>

          <!-- Lista de Mensagens do Chat -->
          <div v-for="(msg, index) in messages" :key="index" class="ai-msg-row"
            :class="msg.sender === 'user' ? 'ai-msg-user' : 'ai-msg-bot'">
            <div class="ai-msg-avatar">
              {{ msg.sender === 'user' ? '👤' : '🤖' }}
            </div>
            <div class="ai-msg-bubble">
              <div class="ai-msg-text" v-html="formatMessage(msg.text)"></div>
              <small class="ai-msg-time">{{ msg.time }}</small>
            </div>
          </div>

          <!-- Indicador de Digitação / Processamento da IA -->
          <div v-if="isTyping" class="ai-msg-row ai-msg-bot">
            <div class="ai-msg-avatar">🤖</div>
            <div class="ai-msg-bubble ai-typing-bubble">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
        </div>

        <!-- Alerta/Banner de Gravação por Voz Ativa -->
        <div v-if="isRecording"
          class="ai-recording-banner d-flex align-items-center justify-content-between px-3 py-1 bg-warning text-dark">
          <small class="d-flex align-items-center font-weight-bold">
            <span class="recording-pulse-dot mr-2"></span>
            {{ recordingBannerText }}
          </small>
          <button class="btn btn-sm p-0 text-dark" @click="stopRecording(false)" title="Cancelar áudio">
            ✕
          </button>
        </div>

        <!-- Input e Envio de Mensagem -->
        <div class="ai-chat-footer">
          <div class="input-group">
            <input type="text" class="form-control ai-chat-input"
              :placeholder="isRecording ? recordingBannerText : 'Digite sua mensagem...'" v-model="inputPrompt"
              @keyup.enter="sendMessage" :disabled="isTyping || isRecording" />
            <div class="input-group-append">
              <!-- Botão do Microfone -->
              <button class="btn ai-mic-btn" :class="{ 'is-recording': isRecording }" type="button"
                @click="toggleRecording" :disabled="isTyping"
                :title="isRecording ? 'Parar e enviar áudio' : 'Falar por áudio'">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              </button>

              <!-- Botão de Enviar -->
              <button class="btn btn-primary ai-send-btn" type="button" @click="sendMessage"
                :disabled="!inputPrompt.trim() || isTyping || isRecording" title="Enviar mensagem">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Botão Flutuante (Avatar / Bonequinho - Drag handle) -->
    <div class="ai-fab-button" :class="{ 'fab-active': isOpen }" @mousedown="startDrag" @touchstart="startDrag"
      @click="handleFabClick" title="MyOracle AI Assistant (Arraste para mover)">
      <div class="ai-fab-glow"></div>
      <div class="ai-fab-avatar">
        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" fill="url(#ai-grad-fab)" />
          <path
            d="M12 5C8.13401 5 5 8.13401 5 12C5 15.866 8.13401 19 12 19C15.866 19 19 15.866 19 12C19 8.13401 15.866 5 12 5Z"
            stroke="#FFFFFF" stroke-width="1.2" />
          <circle cx="9" cy="11.5" r="1.6" fill="#FFFFFF" />
          <circle cx="15" cy="11.5" r="1.6" fill="#FFFFFF" />
          <path d="M12 2V5" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" />
          <circle cx="12" cy="2" r="1.5" fill="#FFD700" />
          <path d="M9 15C10 16 14 16 15 15" stroke="#FFFFFF" stroke-width="1.8" stroke-linecap="round" />
          <defs>
            <linearGradient id="ai-grad-fab" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
              <stop stop-color="#FF7E29" />
              <stop offset="1" stop-color="#803B00" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <span v-if="!isOpen && hasUnread" class="ai-fab-unread-badge">1</span>

      <div v-if="!isOpen" class="ai-fab-tooltip">
        <span>Fale com a IA</span>
      </div>
    </div>
  </div>
</template>

<script>
import { sendAiPrompt, sendAiAudio, resetConversationId } from './aiService';
import {
  pcmChunksToRealtimeWav,
  computeRms,
  SILENCE_TIMEOUT_MS,
  SPEECH_RMS_THRESHOLD,
  SPEECH_CONFIRM_FRAMES
} from './audioUtils';

export default {
  name: 'AiAssistantWidget',
  data() {
    return {
      isOpen: false,
      isMaximized: false,
      inputPrompt: '',
      isTyping: false,
      isRecording: false,
      shouldSendAudio: false,
      hasSpoken: false,
      hasUnread: true,
      messages: [],
      mediaStream: null,
      audioContext: null,
      audioProcessor: null,
      audioSource: null,
      pcmChunks: [],
      recordingSampleRate: 48000,
      speechConfirmCount: 0,
      silenceStartedAt: null,
      isAutoStopping: false,

      // Posicionamento e Drag & Drop
      pos: { x: null, y: null },
      isDragging: false,
      hasMoved: false,
      dragOffset: { x: 0, y: 0 },
      startClickPos: { x: 0, y: 0 },

      suggestions: [
        { icon: '⚡', text: 'Como cadastrar e configurar Recursos?' },
        { icon: '📦', text: 'Como gerenciar Materiais e Roteiros?' },
        { icon: '⚙️', text: 'Como estruturar Centros de Trabalho?' },
        { icon: '🏢', text: 'Como cadastrar Unidades?' }
      ]
    };
  },
  computed: {
    userName() {
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        return userData.first_name || 'Usuário';
      } catch (e) {
        return 'Usuário';
      }
    },
    recordingBannerText() {
      if (!this.hasSpoken) {
        return 'Ouvindo... fale agora';
      }
      return 'Gravando... envia após 6s de silêncio';
    },
    containerStyle() {
      if (this.pos.x !== null && this.pos.y !== null) {
        return {
          left: `${this.pos.x}px`,
          top: `${this.pos.y}px`,
          bottom: 'auto',
          right: 'auto'
        };
      }
      return {};
    }
  },
  mounted() {
    this.loadSavedPosition();
    window.addEventListener('resize', this.clampToBounds);
  },
  beforeDestroy() {
    this.stopRecording(false);
    window.removeEventListener('resize', this.clampToBounds);
  },
  methods: {
    loadSavedPosition() {
      try {
        const saved = localStorage.getItem('ai_widget_position');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
            this.pos.x = parsed.x;
            this.pos.y = parsed.y;
            this.clampToBounds();
          }
        }
      } catch (e) {
        // fallback default
      }
    },
    savePosition() {
      if (this.pos.x !== null && this.pos.y !== null) {
        localStorage.setItem('ai_widget_position', JSON.stringify(this.pos));
      }
    },
    clampToBounds() {
      if (this.pos.x === null || this.pos.y === null) return;
      const maxX = Math.max(10, window.innerWidth - 70);
      const maxY = Math.max(10, window.innerHeight - 70);
      this.pos.x = Math.max(10, Math.min(this.pos.x, maxX));
      this.pos.y = Math.max(10, Math.min(this.pos.y, maxY));
    },

    /* --- Drag & Drop Handlers --- */
    startDrag(e) {
      // Ignorar se o clique foi em um botão de ação no header
      if (e.target.closest('button') || e.target.closest('input')) return;

      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;

      this.startClickPos = { x: clientX, y: clientY };
      this.hasMoved = false;

      const container = this.$el;
      const rect = container.getBoundingClientRect();

      // Se a posição inicial for a padrão (bottom/left via CSS), converter para coordenadas top/left fixas
      if (this.pos.x === null || this.pos.y === null) {
        this.pos.x = rect.left;
        this.pos.y = rect.top;
      }

      this.dragOffset = {
        x: clientX - this.pos.x,
        y: clientY - this.pos.y
      };

      if (e.touches) {
        window.addEventListener('touchmove', this.onDrag, { passive: false });
        window.addEventListener('touchend', this.stopDrag);
      } else {
        window.addEventListener('mousemove', this.onDrag);
        window.addEventListener('mouseup', this.stopDrag);
      }
    },
    onDrag(e) {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;

      const dist = Math.hypot(clientX - this.startClickPos.x, clientY - this.startClickPos.y);
      if (dist > 5) {
        this.hasMoved = true;
        this.isDragging = true;
        if (e.cancelable) e.preventDefault();
      }

      if (this.isDragging) {
        let newX = clientX - this.dragOffset.x;
        let newY = clientY - this.dragOffset.y;

        const maxX = Math.max(10, window.innerWidth - 70);
        const maxY = Math.max(10, window.innerHeight - 70);

        this.pos.x = Math.max(10, Math.min(newX, maxX));
        this.pos.y = Math.max(10, Math.min(newY, maxY));
      }
    },
    stopDrag(e) {
      window.removeEventListener('mousemove', this.onDrag);
      window.removeEventListener('mouseup', this.stopDrag);
      window.removeEventListener('touchmove', this.onDrag);
      window.removeEventListener('touchend', this.stopDrag);

      setTimeout(() => {
        this.isDragging = false;
      }, 50);

      if (this.hasMoved) {
        this.savePosition();
      }
    },
    handleFabClick() {
      // Abre/fecha somente se o usuário clicou sem ter arrastado o widget
      if (!this.hasMoved) {
        this.toggleChat();
      }
    },

    toggleChat() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.hasUnread = false;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } else {
        this.stopRecording(false);
      }
    },
    toggleMaximize() {
      this.isMaximized = !this.isMaximized;
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    resetConversation() {
      resetConversationId();
      this.messages = [];
    },
    async toggleRecording() {
      if (this.isRecording) {
        // Só envia se o usuário já falou; senão cancela
        this.stopRecording(this.hasSpoken);
      } else {
        await this.startRecording();
      }
    },
    async startRecording() {
      try {
        this.cleanupRecordingResources();

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true
          }
        });

        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioCtx();
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        const source = audioContext.createMediaStreamSource(stream);
        const processor = audioContext.createScriptProcessor(4096, 1, 1);
        const mute = audioContext.createGain();
        mute.gain.value = 0;

        this.mediaStream = stream;
        this.audioContext = audioContext;
        this.audioSource = source;
        this.audioProcessor = processor;
        this.pcmChunks = [];
        this.recordingSampleRate = audioContext.sampleRate;
        this.hasSpoken = false;
        this.speechConfirmCount = 0;
        this.silenceStartedAt = null;
        this.isAutoStopping = false;
        this.shouldSendAudio = true;
        this.isRecording = true;

        processor.onaudioprocess = (event) => {
          if (!this.isRecording) return;

          const input = event.inputBuffer.getChannelData(0);
          this.pcmChunks.push(new Float32Array(input));
          this.handleVadFrame(input);
        };

        source.connect(processor);
        processor.connect(mute);
        mute.connect(audioContext.destination);
      } catch (err) {
        console.error('[MyOracle AI] Erro ao acessar microfone:', err);
        this.cleanupRecordingResources();
        this.isRecording = false;
        alert('Não foi possível acessar o microfone. Verifique as permissões do seu navegador.');
      }
    },
    handleVadFrame(samples) {
      const rms = computeRms(samples);
      const isSpeech = rms >= SPEECH_RMS_THRESHOLD;

      if (!this.hasSpoken) {
        if (isSpeech) {
          this.speechConfirmCount += 1;
          if (this.speechConfirmCount >= SPEECH_CONFIRM_FRAMES) {
            this.hasSpoken = true;
            this.silenceStartedAt = null;
          }
        } else {
          this.speechConfirmCount = 0;
        }
        return;
      }

      // Já falou: 6s de silêncio → encerra e envia .wav
      if (isSpeech) {
        this.silenceStartedAt = null;
        return;
      }

      const now = Date.now();
      if (this.silenceStartedAt == null) {
        this.silenceStartedAt = now;
        return;
      }

      if (now - this.silenceStartedAt >= SILENCE_TIMEOUT_MS) {
        if (this.isAutoStopping) return;
        this.isAutoStopping = true;
        // Evita disconnect do ScriptProcessor dentro do próprio onaudioprocess
        setTimeout(() => {
          this.isAutoStopping = false;
          if (this.isRecording && this.hasSpoken) {
            this.stopRecording(true);
          }
        }, 0);
      }
    },
    stopRecording(shouldSend = true) {
      if (!this.isRecording && !this.audioContext && !this.mediaStream) {
        return;
      }

      this.shouldSendAudio = shouldSend && this.hasSpoken;
      this.isRecording = false;
      this.isAutoStopping = false;

      const chunks = this.pcmChunks.slice();
      const sampleRate = this.recordingSampleRate;
      const shouldSubmit = this.shouldSendAudio && chunks.length > 0;

      this.cleanupRecordingResources();
      this.hasSpoken = false;

      if (shouldSubmit) {
        this.handleAudioSubmission(chunks, sampleRate);
      }
    },
    cleanupRecordingResources() {
      if (this.audioProcessor) {
        try {
          this.audioProcessor.onaudioprocess = null;
          this.audioProcessor.disconnect();
        } catch (e) { /* ignore */ }
        this.audioProcessor = null;
      }

      if (this.audioSource) {
        try { this.audioSource.disconnect(); } catch (e) { /* ignore */ }
        this.audioSource = null;
      }

      if (this.audioContext) {
        try { this.audioContext.close(); } catch (e) { /* ignore */ }
        this.audioContext = null;
      }

      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((track) => track.stop());
        this.mediaStream = null;
      }

      this.pcmChunks = [];
      this.speechConfirmCount = 0;
      this.silenceStartedAt = null;
    },
    async handleAudioSubmission(pcmChunks, sampleRate) {
      // Sem transcrição no browser: envia o .wav no lugar de message
      this.messages.push({
        sender: 'user',
        text: '🎤 Mensagem de voz',
        time: this.getCurrentTime()
      });

      this.isTyping = true;
      this.scrollToBottom();

      try {
        const wavBlob = pcmChunksToRealtimeWav(pcmChunks, sampleRate);
        // No áudio a intenção vem na fala; se estiver na tela de apontamento, envia operationId
        const operationId = this.isAppointmentScreen() ? this.getCurrentOperationId() : null;
        const aiReply = await sendAiAudio(wavBlob, { operationId });
        this.messages.push({
          sender: 'bot',
          text: aiReply,
          time: this.getCurrentTime()
        });
      } catch (err) {
        console.error('[MyOracle AI] Erro ao processar/enviar áudio:', err);
        this.messages.push({
          sender: 'bot',
          text: 'Ocorreu uma falha ao enviar o áudio para o servidor MyOracle. Verifique sua conexão e tente novamente.',
          time: this.getCurrentTime()
        });
      } finally {
        this.isTyping = false;
        this.scrollToBottom();
      }
    },
    async sendMessage() {
      if (this.isRecording) {
        this.stopRecording(this.hasSpoken);
        return;
      }

      const prompt = this.inputPrompt.trim();
      if (!prompt || this.isTyping) return;

      const userMsg = {
        sender: 'user',
        text: prompt,
        time: this.getCurrentTime()
      };
      this.messages.push(userMsg);
      this.inputPrompt = '';
      this.isTyping = true;
      this.scrollToBottom();

      try {
        const operationId =
          this.isAppointmentScreen() && this.messageWantsAppointment(prompt)
            ? this.getCurrentOperationId()
            : null;
        const aiReply = await sendAiPrompt(prompt, { operationId });
        this.messages.push({
          sender: 'bot',
          text: aiReply,
          time: this.getCurrentTime()
        });
      } catch (err) {
        this.messages.push({
          sender: 'bot',
          text: 'Desculpe, ocorreu uma falha ao comunicar com a IA do MyOracle. Tente novamente em instantes.',
          time: this.getCurrentTime()
        });
      } finally {
        this.isTyping = false;
        this.scrollToBottom();
      }
    },
    isAppointmentScreen() {
      const route = this.$route || {};
      const path = route.path || window.location.pathname || '';
      const routeName = route.name || '';

      return (
        routeName === 'operator-production-progress' ||
        routeName === 'operator-production-progress-multiple' ||
        routeName === 'operator-point-resource' ||
        path.includes('/operator/production-progress') ||
        path.includes('/operator/point-resource')
      );
    },
    getCurrentOperationId() {
      const params = this.$route?.params || {};
      const operationId = params.order_id || params.operation_id || null;
      if (operationId == null || operationId === '') return null;
      const parsed = parseInt(operationId, 10);
      return Number.isNaN(parsed) ? operationId : parsed;
    },
    messageWantsAppointment(message) {
      if (!message) return false;
      const normalized = message
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

      return (
        /apontament/.test(normalized) ||
        /apontar/.test(normalized) ||
        /lancar produ/.test(normalized) ||
        /registrar produ/.test(normalized) ||
        /fazer apont/.test(normalized)
      );
    },
    sendSuggestion(text) {
      this.inputPrompt = text;
      this.sendMessage();
    },
    getCurrentTime() {
      const now = new Date();
      return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatBody;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    formatMessage(text) {
      if (!text) return '';
      let formatted = text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br/>');
      return formatted;
    }
  }
};
</script>

<style scoped lang="scss">
.ai-assistant-container {
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 999999;
  font-family: 'Raleway', sans-serif;
  touch-action: none;

  &.is-dragging {
    user-select: none;

    .ai-fab-button,
    .ai-chat-header {
      cursor: grabbing !important;
    }
  }
}

.unselectable {
  user-select: none;
}

/* --- Botão Flutuante (FAB) --- */
.ai-fab-button {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FF7E29 0%, #974900 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  box-shadow: 0 8px 24px rgba(151, 73, 0, 0.4);
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  user-select: none;

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 12px 28px rgba(151, 73, 0, 0.55);

    .ai-fab-tooltip {
      opacity: 1;
      visibility: visible;
      transform: translateY(-50%) translateX(0);
    }
  }

  &.fab-active {
    transform: rotate(90deg) scale(0.95);
  }
}

.ai-fab-glow {
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 126, 41, 0.6) 0%, rgba(255, 126, 41, 0) 70%);
  animation: pulse-glow 2.5s infinite;
  pointer-events: none;
}

@keyframes pulse-glow {
  0% {
    transform: scale(0.95);
    opacity: 0.8;
  }

  50% {
    transform: scale(1.15);
    opacity: 0.3;
  }

  100% {
    transform: scale(0.95);
    opacity: 0.8;
  }
}

.ai-fab-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.ai-fab-unread-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background-color: #e53e3e;
  color: #ffffff;
  font-size: 11px;
  font-weight: bold;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
  z-index: 3;
}

.ai-fab-tooltip {
  position: absolute;
  left: 72px;
  top: 50%;
  transform: translateY(-50%) translateX(-10px);
  background: rgba(30, 30, 30, 0.9);
  color: #ffffff;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.25s ease;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
  pointer-events: none;
}

/* --- Janela de Chat --- */
.ai-chat-window {
  position: absolute;
  bottom: 76px;
  left: 0;
  width: 380px;
  max-width: calc(100vw - 32px);
  height: 540px;
  max-height: calc(100vh - 120px);
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(0, 0, 0, 0.08);
  z-index: 1000;
  transition: width 0.35s cubic-bezier(0.16, 1, 0.3, 1), height 0.35s cubic-bezier(0.16, 1, 0.3, 1);

  &.is-maximized {
    width: 820px;
    max-width: calc(92vw - 32px);
    height: 700px;
    max-height: calc(85vh - 60px);
  }
}

/* Header */
.ai-chat-header {
  background: linear-gradient(135deg, #FF7E29 0%, #974900 100%);
  padding: 14px 18px;
  color: #ffffff;
  cursor: grab;
}

.ai-avatar-badge {
  position: relative;
}

.online-indicator {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 8px;
  height: 8px;
  background-color: #38a169;
  border-radius: 50%;
  border: 1.5px solid #ffffff;
}

/* Banner de Gravação de Voz */
.ai-recording-banner {
  border-bottom: 1px solid #ecc94b;
  font-size: 12px;
}

.recording-pulse-dot {
  width: 10px;
  height: 10px;
  background-color: #e53e3e;
  border-radius: 50%;
  display: inline-block;
  animation: pulse-red 1s infinite;
}

@keyframes pulse-red {
  0% {
    transform: scale(0.9);
    opacity: 1;
  }

  50% {
    transform: scale(1.3);
    opacity: 0.5;
  }

  100% {
    transform: scale(0.9);
    opacity: 1;
  }
}

/* Corpo / Mensagens */
.ai-chat-body {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  background-color: #f8f9fa;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 4px;
  }
}

.ai-msg-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  max-width: 86%;

  &.ai-msg-bot {
    align-self: flex-start;
  }

  &.ai-msg-user {
    align-self: flex-end;
    flex-direction: row-reverse;

    .ai-msg-bubble {
      background: linear-gradient(135deg, #974900 0%, #733700 100%);
      color: #ffffff;
      border-bottom-right-radius: 4px;
      border-bottom-left-radius: 16px;
    }

    .ai-msg-time {
      color: rgba(255, 255, 255, 0.7);
    }
  }
}

.ai-msg-avatar {
  font-size: 18px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

.ai-msg-bubble {
  background: #ffffff;
  padding: 10px 14px;
  border-radius: 16px;
  border-bottom-left-radius: 4px;
  color: #2d3748;
  font-size: 13.5px;
  line-height: 1.45;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  word-break: break-word;
}

.ai-msg-time {
  display: block;
  font-size: 10px;
  color: #a0aec0;
  margin-top: 4px;
  text-align: right;
}

.ai-suggestion-chip {
  font-size: 12px;
  padding: 6px 12px;
  border-color: #e2e8f0;
  color: #4a5568;
  background: #ffffff;
  transition: all 0.2s ease;

  &:hover {
    background: #fff5ed;
    border-color: #ff7e29;
    color: #974900;
  }
}

/* Indicador de Digitação */
.ai-typing-bubble {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;

  .dot {
    width: 6px;
    height: 6px;
    background-color: #a0aec0;
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out both;

    &:nth-child(1) {
      animation-delay: 0s;
    }

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes typing {

  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.4;
  }

  40% {
    transform: scale(1.1);
    opacity: 1;
  }
}

/* Footer / Input */
.ai-chat-footer {
  padding: 12px;
  background: #ffffff;
  border-top: 1px solid #edf2f7;
}

.ai-chat-input {
  border-radius: 20px 0 0 20px !important;
  font-size: 13.5px;
  padding-left: 16px;
  border-color: #e2e8f0;

  &:focus {
    border-color: #ff7e29;
    box-shadow: 0 0 0 3px rgba(255, 126, 41, 0.15);
  }
}

/* Botão de Microfone */
.ai-mic-btn {
  border-radius: 0 !important;
  background: #f7fafc;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
  border-left: none;
  border-right: none;
  color: #718096;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    color: #ff7e29;
    background: #fff5ed;
  }

  &.is-recording {
    background: #fff5f5;
    color: #e53e3e;
    animation: pulse-mic 1.2s infinite;
  }
}

@keyframes pulse-mic {
  0% {
    color: #e53e3e;
  }

  50% {
    color: #c53030;
  }

  100% {
    color: #e53e3e;
  }
}

.ai-send-btn {
  border-radius: 0 20px 20px 0 !important;
  background: linear-gradient(135deg, #FF7E29 0%, #974900 100%) !important;
  border: none !important;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.9;
  }
}

/* Transições */
.ai-chat-slide-enter-active,
.ai-chat-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.ai-chat-slide-enter,
.ai-chat-slide-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}
</style>
